import { Component } from '@theme/component';
import { parseAmount, parseUsername, parseUsernameInput, parseWhatsApp, liveOrderProperties, verifyLiveLine, estimateLiveOrder } from '@theme/wm-live-validation';

/**
 * Live order enhancement. Shopify remains the source of price and cart truth.
 * @extends {Component<{form: HTMLFormElement, amount: HTMLInputElement, username: HTMLInputElement, usernameProperty: HTMLInputElement, whatsapp?: HTMLInputElement, total: HTMLOutputElement, otherRow: HTMLElement, combinedRow: HTMLElement, combinedTotal: HTMLOutputElement, otherTotal: HTMLOutputElement, shipping: HTMLOutputElement, estimateTotal: HTMLOutputElement, estimateNotice: HTMLElement, submit: HTMLButtonElement, status: HTMLElement, config: HTMLScriptElement}>}
 */
class LiveOrder extends Component {
  #config;
  #busy = false;
  #abort;
  #cart = null;
  #cartRequest = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    this.#initialize();
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.refreshCart();
    }, { signal: this.#abort.signal });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    super.disconnectedCallback();
  }

  #initialize() {
    this.#config = JSON.parse(this.refs.config.textContent);
    if (!this.refs.form) return;
    if (this.dataset.ready !== 'true') return;
    this.refs.submit.disabled = this.#busy;
    // Tab-local storage preserves the form when the store is opened separately.
    try {
      const saved = JSON.parse(sessionStorage.getItem('wm-live-order') || '{}');
      if (!this.refs.amount.value) this.refs.amount.value = saved.amount || '';
      if (!this.refs.username.value) this.refs.username.value = saved.username || '';
    } catch { /* Storage may be disabled; the form still works. */ }
    this.updateUsername();
    this.updateWhatsApp();
    this.refreshCart();
  }

  async refreshCart() {
    if (!this.refs.form || this.dataset.ready !== 'true') return;
    const request = ++this.#cartRequest;
    try {
      const cart = await this.#request('cart.js');
      if (request !== this.#cartRequest) return;
      this.#cart = cart;
    } catch {
      if (request !== this.#cartRequest || this.#abort.signal.aborted) return;
      this.#cart = null;
    }
    this.updateAmount();
  }

  #money(cents) {
    return `${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100)} USD`;
  }

  updateUsername() {
    const input = this.refs.username;
    const prefixed = parseUsername(input.value);
    // Migrate old saved values and pasted full handles without accepting @@name.
    if (prefixed) input.value = prefixed.slice(1);
    else if (input.value.trim() === '@') input.value = '';
    input.removeAttribute('aria-invalid');
    this.updateAmount();
  }

  updateWhatsApp() {
    const input = this.refs.whatsapp;
    if (!input) return;
    const invalid = parseWhatsApp(input.value) === null;
    input.setCustomValidity(invalid ? this.#config.messages.whatsapp : '');
    if (!invalid) input.removeAttribute('aria-invalid');
  }

  updateAmount() {
    const amount = parseAmount(this.refs.amount.value, this.#config);
    this.refs.total.textContent = amount === null ? '—' : this.#money(amount * 100);
    const username = parseUsernameInput(this.refs.username.value);
    this.refs.usernameProperty.value = username || '';
    const estimate = estimateLiveOrder(this.#cart, this.#config.variantId, amount, username, this.#config);
    this.refs.otherRow.hidden = !estimate?.otherCents;
    this.refs.combinedRow.hidden = !estimate?.otherCents;
    this.refs.combinedTotal.textContent = estimate ? this.#money(estimate.merchandiseCents) : '—';
    this.refs.estimateNotice.classList.toggle('wm-live__cart-warning', Boolean(estimate?.otherCents));
    this.refs.otherTotal.textContent = estimate ? this.#money(estimate.otherCents) : '—';
    this.refs.shipping.textContent = estimate ? (estimate.shippingCents === 0 ? this.#config.estimateMessages.free : this.#money(estimate.shippingCents)) : '—';
    this.refs.estimateTotal.textContent = estimate ? this.#money(estimate.totalCents) : '—';
    this.refs.estimateNotice.textContent = estimate ? (estimate.otherCents ? this.#config.estimateMessages.other.replace('[amount]', this.#money(estimate.otherCents)) : '')
      : (amount === null ? this.#config.estimateMessages.pending : this.#config.estimateMessages.unavailable);
    this.refs.amount.removeAttribute('aria-invalid');
    this.#save();
  }

  #save() {
    try { sessionStorage.setItem('wm-live-order', JSON.stringify({ amount: this.refs.amount.value, username: this.refs.username.value })); } catch { /* Optional storage. */ }
  }

  async #request(path, payload) {
    const response = await fetch(`${this.dataset.root.replace(/\/$/, '')}/${path}`, {
      method: payload ? 'POST' : 'GET',
      headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) },
      body: payload ? JSON.stringify(payload) : undefined,
      credentials: 'same-origin', cache: 'no-store', signal: this.#abort.signal,
    });
    if (!response.ok) throw new Error(this.#config.messages.network);
    return response.json();
  }

  /** @param {SubmitEvent} event */
  async submitOrder(event) {
    event.preventDefault();
    if (this.#busy) return;
    const { amount: amountInput, username: usernameInput, whatsapp: whatsappInput, submit, status } = this.refs;
    status.textContent = '';
    const amount = parseAmount(amountInput.value, this.#config);
    const username = parseUsernameInput(usernameInput.value);
    const whatsapp = parseWhatsApp(whatsappInput?.value || '');
    usernameInput.removeAttribute('aria-invalid');
    if (amount === null || username === null || whatsapp === null) {
      const field = amount === null ? amountInput : username === null ? usernameInput : whatsappInput;
      field.setAttribute('aria-invalid', 'true');
      status.textContent = amount === null ? this.#config.messages.amount : username === null ? this.#config.messages.username : this.#config.messages.whatsapp;
      field.focus();
      return;
    }
    this.refs.usernameProperty.value = username;
    this.#save();
    this.#busy = true;
    submit.disabled = true;
    submit.textContent = this.#config.messages.working;
    try {
      const run = async () => {
        const cart = await this.#request('cart.js');
        if (cart.currency !== 'USD' || this.#config.price !== 100) throw new Error(this.#config.messages.unavailable);
        const productResponse = await fetch(`${this.dataset.productUrl}.js`, { credentials: 'same-origin', cache: 'no-store', signal: this.#abort.signal });
        if (!productResponse.ok) throw new Error(this.#config.messages.unavailable);
        const product = await productResponse.json();
        const variant = product.variants.find((item) => String(item.id) === String(this.#config.variantId));
        if (!variant?.available || variant.price !== 100) throw new Error(this.#config.messages.unavailable);
        const matching = cart.items.filter((item) => String(item.variant_id) === String(this.#config.variantId)
          && item.properties?.['TikTok username'] === username);
        if (matching.length > 1) throw new Error(this.#config.messages.cart);
        const properties = liveOrderProperties(matching[0]?.properties, username, whatsapp, this.#config.offerRule);
        if (matching.length === 1) {
          // Absolute quantity makes a retry after a dropped response idempotent.
          await this.#request('cart/change.js', { id: matching[0].key, quantity: amount, properties });
        } else {
          await this.#request('cart/add.js', { items: [{ id: this.#config.variantId, quantity: amount, properties }] });
        }
        const verified = await this.#request('cart.js');
        if (!verifyLiveLine(verified, this.#config.variantId, amount, username, whatsapp)) throw new Error(this.#config.messages.cart);
        // The host packs bonus sets using the paid-set count for the whole order.
        // Record the offer, without inferring physical sets from arbitrary USD units.
        if (this.#config.offerRule && !verified.items.some((item) =>
          String(item.variant_id) === String(this.#config.variantId)
          && item.properties?.['TikTok username'] === username
          && item.properties?.['LIVE offer'] === this.#config.offerRule)) throw new Error(this.#config.messages.cart);
        // Submit Shopify's native cart checkout action after verification.
        // Every other live item must pass the same guard used on the cart page.
        for (const item of verified.items.filter((line) => String(line.variant_id) === String(this.#config.variantId))) {
          const handle = parseUsername(item.properties?.['TikTok username']);
          if (!handle || !verifyLiveLine(verified, this.#config.variantId, item.quantity, handle)) throw new Error(this.#config.messages.cart);
        }
        const checkout = document.createElement('form');
        checkout.method = 'post';
        checkout.action = this.dataset.cart;
        const action = document.createElement('input');
        action.type = 'hidden';
        action.name = 'checkout';
        action.value = 'Checkout';
        checkout.append(action);
        document.body.append(checkout);
        checkout.submit();
      };
      // Coordinate submissions in different tabs when Web Locks is supported.
      if (navigator.locks) await navigator.locks.request('wm-live-cart', { signal: this.#abort.signal }, run);
      else await run();
    } catch (error) {
      if (error.name === 'AbortError') return;
      status.textContent = Object.values(this.#config.messages).includes(error.message)
        ? error.message : this.#config.messages.network;
      status.focus();
    } finally {
      this.#busy = false;
      submit.disabled = false;
      submit.textContent = this.#config.messages.ready;
    }
  }
}

if (!customElements.get('wm-live-order')) customElements.define('wm-live-order', LiveOrder);
